/**
 * Task Firestore Converter (Client SDK)
 *
 * Client-side converter for use with firebase/firestore (NOT firebase-admin).
 * Converts between Task TypeScript type and Firestore data.
 *
 * Based on schema-generated converter pattern but adapted for client SDK.
 * Handles Date↔Timestamp conversion for createdAt and updatedAt fields.
 *
 * Sprint 5 (T012.1C)
 */

import { FirestoreDataConverter, QueryDocumentSnapshot, SnapshotOptions, Timestamp } from 'firebase/firestore';
import type { Task } from '@segmently/schema-models/entities';

export const taskConverter: FirestoreDataConverter<Task> = {
  /**
   * Convert Task model to Firestore data
   * Called when writing to Firestore
   *
   * Handles conversion of Date fields to Firestore Timestamps:
   * - createdAt: ISO string → Timestamp
   * - updatedAt: ISO string → Timestamp
   */
  toFirestore(task: Task): any {
    const result: any = { ...task };

    // Convert ISO string dates to Firestore Timestamps
    if (typeof result['createdAt'] === 'string') {
      result['createdAt'] = Timestamp.fromDate(new Date(result['createdAt']));
    } else if (result['createdAt'] instanceof Date) {
      result['createdAt'] = Timestamp.fromDate(result['createdAt']);
    }

    if (typeof result['updatedAt'] === 'string') {
      result['updatedAt'] = Timestamp.fromDate(new Date(result['updatedAt']));
    } else if (result['updatedAt'] instanceof Date) {
      result['updatedAt'] = Timestamp.fromDate(result['updatedAt']);
    }

    return result;
  },

  /**
   * Convert Firestore data to Task model
   * Called when reading from Firestore
   *
   * Handles conversion of Firestore Timestamps to ISO strings:
   * - createdAt: Timestamp → ISO string
   * - updatedAt: Timestamp → ISO string
   */
  fromFirestore(
    snapshot: QueryDocumentSnapshot,
    options?: SnapshotOptions
  ): Task {
    const data = snapshot.data(options);
    const result: any = { ...data };

    // Convert Firestore Timestamps to Date
    if (result['createdAt']?.toDate) {
      result['createdAt'] = result['createdAt'].toDate();
    } else if (typeof result['createdAt'] === 'string') {
      result['createdAt'] = new Date(result['createdAt']);
    }

    if (result['updatedAt']?.toDate) {
      result['updatedAt'] = result['updatedAt'].toDate();
    } else if (typeof result['updatedAt'] === 'string') {
      result['updatedAt'] = new Date(result['updatedAt']);
    }

    return result as Task;
  }
};

export default taskConverter;
